import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Code2,
  Copy,
  FileText,
  Globe,
  ImagePlus,
  Lightbulb,
  Loader2,
  MessageCircle,
  Search,
  ScanSearch,
  Terminal,
  Undo2,
  Volume2,
  Wrench,
} from 'lucide-react';
import axios from 'axios';
import { agentToolLabel } from '../utils/agentTools';

const authHeaders = () => {
  const token = localStorage.getItem('authToken') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const domainFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (_) {
    return '';
  }
};

const faviconUrl = (url) => {
  const host = domainFromUrl(url);
  if (!host) return '';
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
};

const statusOf = (s) => {
  if (s === 'running' || s === 'thinking' || s === 'active') return 'active';
  if (s === 'error') return 'error';
  return 'done';
};

/** Markdown-Fences aus Preview entfernen (Modell/Server-Format) */
function stripCodeFences(text) {
  let s = String(text || '').trim();
  s = s.replace(/^```[a-zA-Z0-9_+.#-]*[^\n]*\n?/, '');
  s = s.replace(/\n?```\s*$/, '');
  // „Datei path:\n```…“ Prefix
  s = s.replace(/^Datei\s+[^\n]+:\s*/i, '');
  return s.trim();
}

function langFromPath(filePath) {
  const ext = String(filePath || '')
    .split('.')
    .pop()
    .toLowerCase();
  const map = {
    js: 'javascript',
    jsx: 'javascript',
    ts: 'typescript',
    tsx: 'typescript',
    css: 'css',
    html: 'html',
    json: 'json',
    md: 'markdown',
    py: 'python',
    sh: 'bash',
    yml: 'yaml',
    yaml: 'yaml',
  };
  return map[ext] || ext || 'text';
}

function formatTreePreview(tree, depth = 0, lines = [], maxLines = 40) {
  if (!Array.isArray(tree) || lines.length >= maxLines) return lines;
  for (const node of tree) {
    if (lines.length >= maxLines) break;
    const name = node?.name || node?.path || '?';
    const pad = '  '.repeat(depth);
    if (node?.type === 'dir' || Array.isArray(node?.children)) {
      lines.push(`${pad}${name}/`);
      formatTreePreview(node.children || [], depth + 1, lines, maxLines);
    } else {
      const size = node?.size != null ? ` (${node.size})` : '';
      lines.push(`${pad}${name}${size}`);
    }
  }
  return lines;
}

/**
 * Baut aus Pipeline / Agent-Schritten / Suche eine DeepSeek-ähnliche Timeline.
 */
export function buildResearchModel({
  pipeline = [],
  phase = '',
  phaseMessage = '',
  agentSteps = [],
  search = null,
  streaming = false,
  thinkingText = '',
  agentMode = false,
}) {
  const sources = Array.isArray(search?.sources) ? search.sources.filter((s) => s?.url) : [];
  const queries = Array.isArray(search?.queries)
    ? search.queries.map(String).filter(Boolean)
    : search?.query
      ? [String(search.query)]
      : [];
  const thinkBody = String(thinkingText || search?.thinking || '').trim();

  const thinkChild = (fallback) => [
    {
      type: 'think',
      // Genug Text für Markieren mitten im Stream (nicht nur Anfang/Ende)
      text: (thinkBody || fallback || '').slice(0, 50000) || '',
    },
  ].filter((c) => c.text);

  const steps = [];

  // Agent: nur Tool-Schritte — keine Chat-Pipeline Start→Denken→Schreiben
  if (agentMode && !(Array.isArray(agentSteps) && agentSteps.length)) {
    if (streaming) {
      steps.push({
        id: 'agent-waiting',
        kind: 'thinking',
        title: phaseMessage && !/^Denkt nach/i.test(phaseMessage) ? phaseMessage : 'Plant nächsten Schritt',
        status: 'active',
        badge: thinkBody ? `${Math.max(1, thinkBody.split(/\s+/).length)} w` : null,
        children: thinkBody ? thinkChild('') : [],
      });
    }
  } else if (Array.isArray(agentSteps) && agentSteps.length) {
    agentSteps.forEach((raw, i) => {
      if (!raw) return;
      const st = statusOf(raw.status);
      const tool = String(raw.tool || '').toLowerCase();
      const details = raw.details || {};
      const args = raw.args || {};

      if (raw.status === 'thinking' || (tool === '' && (details.thought || raw.preview || raw.thought))) {
        const stored = String(details.thought || raw.preview || raw.thought || '')
          .trim()
          .slice(0, 6000);
        const thought =
          st === 'active' && thinkBody
            ? thinkBody.slice(0, 50000)
            : stored || (st === 'active' ? thinkBody.slice(0, 50000) : '');
        const rawLabel = String(raw.label || '').trim();
        const title =
          rawLabel && !/^Schreibt\b/i.test(rawLabel) && !/^Denkt nach/i.test(rawLabel)
            ? rawLabel
            : 'Thinking';
        const wordBadge = thought ? `${Math.max(1, thought.split(/\s+/).length)} w` : null;
        steps.push({
          id: `think-${raw.index ?? i}-${i}`,
          kind: 'thinking',
          title,
          status: st === 'active' ? 'active' : 'done',
          badge: wordBadge,
          children: thought
            ? [{ type: 'think', text: thought }]
            : st === 'active'
              ? []
              : [
                  {
                    type: 'think',
                    text: 'Überlegung abgeschlossen — keine ausführlichen Thinking-Tokens vom Modell.',
                  },
                ],
        });
        return;
      }

      if (tool === 'web_search' || tool === 'websuche') {
        const qList =
          (Array.isArray(details.queries) && details.queries.length
            ? details.queries
            : args.query
              ? [args.query]
              : queries
          ).map(String).filter(Boolean);
        steps.push({
          id: `search-${raw.index ?? i}`,
          kind: 'search',
          title: qList[0]
            ? `Searching ${String(qList[0]).slice(0, 64)}`
            : agentToolLabel(tool, raw.label) || 'Searching',
          status: st,
          badge: qList.length ? `${qList.length} quer${qList.length === 1 ? 'y' : 'ies'}` : null,
          children: qList.map((q) => ({ type: 'query', text: q })),
        });
        return;
      }

      if (tool === 'fetch_url' || tool === 'seite_laden') {
        const pages =
          Array.isArray(details.pages) && details.pages.length
            ? details.pages
            : args.url
              ? [{ title: args.url, url: args.url }]
              : [];
        const pageChildren =
          pages.length > 0
            ? pages.map((p) => ({
                type: 'page',
                title: p.title || p.url || 'Seite',
                url: p.url || '',
                domain: domainFromUrl(p.url),
              }))
            : sources
                .filter((s) => s.engine === 'page')
                .map((s) => ({
                  type: 'page',
                  title: s.title || s.url,
                  url: s.url,
                  domain: domainFromUrl(s.url),
                }));
        steps.push({
          id: `read-${raw.index ?? i}`,
          kind: 'reading',
          title:
            pageChildren.length > 0
              ? `Reading ${pageChildren.length} page${pageChildren.length === 1 ? '' : 's'}`
              : agentToolLabel(tool, raw.label) || 'Reading',
          status: st,
          badge: pageChildren.length
            ? `${pageChildren.length} page${pageChildren.length === 1 ? '' : 's'}`
            : null,
          children: pageChildren,
        });
        return;
      }

      if (tool === 'read_file' || tool === 'datei_lesen') {
        const filePath = String(details.path || args.path || '').trim();
        const code = stripCodeFences(
          details.content != null ? details.content : raw.preview || ''
        );
        steps.push({
          id: `file-read-${raw.index ?? i}`,
          kind: 'code',
          title: filePath
            ? `Datei lesen: ${filePath}`
            : agentToolLabel(tool, raw.label) || 'Datei lesen',
          status: st,
          badge:
            st === 'done' || st === 'error'
              ? filePath
                ? `${filePath.split('/').pop()} · fertig`
                : 'fertig'
              : filePath
                ? filePath.split('/').pop()
                : null,
          children: code
            ? [
                {
                  type: 'code',
                  path: filePath,
                  language: langFromPath(filePath),
                  text: code.slice(0, 6000),
                },
              ]
            : filePath
              ? [{ type: 'file', path: filePath, text: filePath }]
              : [],
        });
        return;
      }

      if (tool === 'write_file' || tool === 'datei_schreiben') {
        const filePath = String(details.path || args.path || '').trim();
        const diffLines = Array.isArray(details.diff?.lines) ? details.diff.lines : [];
        const children = [];
        if (details.diff?.unchanged) {
          children.push({ type: 'note', text: 'Keine inhaltliche Änderung' });
        } else if (diffLines.length) {
          children.push({
            type: 'diff',
            path: filePath,
            created: !!details.diff?.created || !!details.created,
            lines: diffLines,
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
            content: details.content != null ? String(details.content) : undefined,
          });
        } else if (details.content) {
          children.push({
            type: 'code',
            path: filePath,
            language: langFromPath(filePath),
            text: String(details.content).slice(0, 6000),
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
          });
        } else if (filePath) {
          children.push({
            type: 'file',
            path: filePath,
            text: `Geschrieben: ${filePath}`,
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
          });
        }
        steps.push({
          id: `file-write-${raw.index ?? i}`,
          kind: 'code',
          title: filePath
            ? details.rejected
              ? `Schreibversuch abgelehnt: ${filePath}`
              : details.created || details.diff?.created
                ? `Datei neu: ${filePath}`
                : `Datei geändert: ${filePath}`
            : agentToolLabel(tool, raw.label) || 'Datei schreiben',
          status: details.rejected ? 'error' : st,
          badge: details.size != null ? `${details.size} B` : filePath ? filePath.split('/').pop() : null,
          children,
          revisionId: details.revisionId || null,
          filePath: filePath || null,
          rejected: !!details.rejected,
        });
        return;
      }

      if (
        tool === 'edit_file' ||
        tool === 'datei_aendern' ||
        tool === 'datei_ändern' ||
        tool === 'search_replace' ||
        tool === 'patch'
      ) {
        const filePath = String(details.path || args.path || '').trim();
        const diffLines = Array.isArray(details.diff?.lines) ? details.diff.lines : [];
        const children = [];
        if (details.diff?.unchanged) {
          children.push({ type: 'note', text: 'Keine inhaltliche Änderung' });
        } else if (diffLines.length) {
          children.push({
            type: 'diff',
            path: filePath,
            created: false,
            lines: diffLines,
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
            content: details.content != null ? String(details.content) : undefined,
          });
        } else if (details.content) {
          children.push({
            type: 'code',
            path: filePath,
            language: langFromPath(filePath),
            text: String(details.content).slice(0, 6000),
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
          });
        } else if (filePath) {
          children.push({
            type: 'file',
            path: filePath,
            text: `Geändert: ${filePath}`,
            revisionId: details.revisionId || null,
            rejected: !!details.rejected,
          });
        }
        const occ =
          details.occurrences != null
            ? `${details.occurrences}×`
            : args.replace_all
              ? 'all'
              : null;
        steps.push({
          id: `file-edit-${raw.index ?? i}`,
          kind: 'code',
          title: filePath
            ? details.rejected
              ? `Patch zurückgesetzt: ${filePath}`
              : `Patch: ${filePath}`
            : agentToolLabel(tool, raw.label) || 'Datei ändern',
          status: details.rejected ? 'error' : st,
          badge: occ || (filePath ? filePath.split('/').pop() : null),
          children,
          revisionId: details.revisionId || null,
          filePath: filePath || null,
          rejected: !!details.rejected,
        });
        return;
      }

      if (tool === 'list_files' || tool === 'dateien_listen') {
        const treeLines = formatTreePreview(details.tree);
        const treeText =
          treeLines.length > 0
            ? treeLines.join('\n')
            : stripCodeFences(raw.preview || '').replace(/^Dateibaum:\s*/i, '');
        steps.push({
          id: `list-${raw.index ?? i}`,
          kind: 'code',
          title: agentToolLabel(tool, raw.label) || 'Dateien listen',
          status: st,
          badge:
            st === 'done' || st === 'error'
              ? Array.isArray(details.tree)
                ? `${details.tree.length} · fertig`
                : 'fertig'
              : Array.isArray(details.tree)
                ? `${details.tree.length}`
                : null,
          children: treeText
            ? [{ type: 'code', path: 'Dateibaum', language: 'text', text: treeText.slice(0, 4000) }]
            : [],
        });
        return;
      }

      if (tool === 'search_files' || tool === 'dateien_suchen' || tool === 'grep') {
        const q = String(args.query || details.query || raw.label || '').trim();
        const matches = Array.isArray(details.matches) ? details.matches : [];
        steps.push({
          id: `search-files-${raw.index ?? i}`,
          kind: 'code',
          title: q ? `Suche: ${q.slice(0, 64)}` : agentToolLabel(tool, raw.label) || 'Dateien suchen',
          status: st,
          badge: matches.length ? `${matches.length}` : null,
          children: matches.length
            ? matches.slice(0, 24).map((m) => ({
                type: 'note',
                text: `${m.path}:${m.line}  ${String(m.text || '').slice(0, 140)}`,
              }))
            : [{ type: 'note', text: 'Keine Treffer' }],
        });
        return;
      }

      let kind = 'tool';
      if (tool.includes('preview') || tool.includes('live') || tool === 'run_check' || tool === 'pruefen') {
        kind = 'terminal';
      } else if (tool.includes('image') || tool.includes('bild')) {
        kind = 'image';
      } else if (
        tool.includes('sound') ||
        tool.includes('audio') ||
        tool.includes('ton') ||
        tool === 'tts'
      ) {
        kind = 'sound';
      } else if (tool.includes('pdf') || tool.includes('dokument')) {
        kind = 'pdf';
      } else if (
        tool.includes('create_project') ||
        tool.includes('projekt_neu') ||
        tool.includes('switch_project') ||
        tool.includes('list_projects') ||
        tool.includes('rename_project') ||
        tool.includes('delete_project') ||
        tool.includes('projekt_wechseln') ||
        tool.includes('projekte_listen')
      ) {
        kind = 'code';
      } else if (
        tool.includes('file') ||
        tool.includes('datei') ||
        tool.includes('mkdir') ||
        tool.includes('scaffold') ||
        tool.includes('projekt') ||
        tool.includes('move_path') ||
        tool.includes('verschieben')
      ) {
        kind = 'code';
      } else if (tool.includes('ask') || tool.includes('frage') || tool.includes('nachfragen')) {
        kind = 'ask';
      }

      const childText = stripCodeFences(
        raw.preview ||
          args.path ||
          args.prompt ||
          args.question ||
          args.command ||
          args.template ||
          ''
      );
      const pathHint = String(args.path || details.path || '').trim();
      steps.push({
        id: `tool-${raw.index ?? i}-${tool}`,
        kind,
        title:
          pathHint && (tool.includes('file') || tool.includes('datei') || tool.includes('mkdir'))
            ? `${agentToolLabel(tool, raw.label) || tool}: ${pathHint}`
            : agentToolLabel(tool, raw.label) || tool || 'Schritt',
        status: st,
        badge: pathHint ? pathHint.split('/').pop() : null,
        children: childText
          ? pathHint && kind === 'code'
            ? [{ type: 'code', path: pathHint, language: langFromPath(pathHint), text: childText.slice(0, 2800) }]
            : [{ type: 'note', text: String(childText).slice(0, 280) }]
          : [],
      });
    });
  } else if (Array.isArray(pipeline) && pipeline.length && streaming && !agentMode) {
    const order = pipeline;
    const currentIdx = order.indexOf(phase);
    const focusIdx = currentIdx >= 0 ? currentIdx : 0;
    let thinkingAdded = false;
    order.forEach((stepKey, idx) => {
      const done =
        idx < focusIdx || (phase === 'write' && stepKey === 'write' && !streaming);
      const active = streaming && idx === focusIdx;
      if (stepKey === 'prepare' || stepKey === 'think') {
        // Nur ein Thinking-Schritt — prepare+think sonst doppelte „Schreibt…“-Karten
        if (thinkingAdded) return;
        thinkingAdded = true;
        const progressOnly = /^Schreibt\b/i.test(String(phaseMessage || ''));
        const thinkActive =
          streaming &&
          (phase === 'prepare' || phase === 'think' || phase === 'agent' || !phase);
        steps.push({
          id: 'phase-thinking',
          kind: 'thinking',
          title: 'Thinking',
          status: thinkActive ? 'active' : done ? 'done' : 'pending',
          badge: thinkBody ? 'details' : null,
          children: thinkChild(
            progressOnly
              ? thinkBody || 'Denkt nach…'
              : thinkActive
                ? phaseMessage || 'Denkt nach…'
                : phaseMessage || 'Recherche vorbereitet.'
          ),
        });
      } else if (stepKey === 'search') {
        steps.push({
          id: 'phase-search',
          kind: 'search',
          title: queries[0]
            ? `Searching ${String(queries[0]).slice(0, 64)}`
            : 'Searching…',
          status: active ? 'active' : done || sources.length ? 'done' : 'pending',
          badge: queries.length ? `${queries.length} quer${queries.length === 1 ? 'y' : 'ies'}` : null,
          children: queries.map((q) => ({ type: 'query', text: q })),
        });
      } else if (stepKey === 'files') {
        steps.push({
          id: 'phase-files',
          kind: 'code',
          title: 'Reading attachments',
          status: active ? 'active' : done ? 'done' : 'pending',
          badge: null,
          children: [],
        });
      } else if (stepKey === 'image') {
        steps.push({
          id: 'phase-image',
          kind: 'image',
          title: 'Generating image',
          status: active ? 'active' : done ? 'done' : 'pending',
          badge: null,
          children: [],
        });
      } else if (stepKey === 'sound') {
        steps.push({
          id: 'phase-sound',
          kind: 'sound',
          title: 'Generating sound',
          status: active ? 'active' : done ? 'done' : 'pending',
          badge: null,
          children: [],
        });
      } else if (stepKey === 'pdf') {
        steps.push({
          id: 'phase-pdf',
          kind: 'pdf',
          title: 'Generating PDF',
          status: active ? 'active' : done ? 'done' : 'pending',
          badge: null,
          children: [],
        });
      } else if (stepKey === 'agent') {
        const writing = /^Schreibt\b/i.test(String(phaseMessage || ''));
        steps.push({
          id: 'phase-agent',
          kind: writing ? 'code' : 'tool',
          title: phaseMessage || 'Agent working',
          status: active || phase === 'agent' ? 'active' : done ? 'done' : 'pending',
          badge: null,
          children: writing
            ? [{ type: 'note', text: String(phaseMessage) }]
            : [],
        });
      }
    });

    if (sources.length && !steps.some((s) => s.kind === 'reading')) {
      const searchIdx = steps.findIndex((s) => s.kind === 'search');
      const readingStep = {
        id: 'phase-reading',
        kind: 'reading',
        title: `Reading ${sources.length} page${sources.length === 1 ? '' : 's'}`,
        status:
          phase === 'think' || phase === 'write' || !streaming
            ? 'done'
            : streaming && phase === 'search'
              ? 'pending'
              : 'active',
        badge: `${sources.length} page${sources.length === 1 ? '' : 's'}`,
        children: sources.slice(0, 12).map((s) => ({
          type: 'page',
          title: s.title || s.url,
          url: s.url,
          domain: domainFromUrl(s.url),
        })),
      };
      if (searchIdx >= 0) steps.splice(searchIdx + 1, 0, readingStep);
      else steps.push(readingStep);
    }
  } else if (queries.length || sources.length) {
    steps.push({
      id: 'done-thinking',
      kind: 'thinking',
      title: 'Thinking',
      status: 'done',
      badge: thinkBody ? 'details' : null,
      children: thinkChild(
        queries.length
          ? `Recherche mit ${queries.length} Suchanfrage${queries.length === 1 ? '' : 'n'} geplant.`
          : 'Recherche vorbereitet.'
      ),
    });
    if (queries.length) {
      steps.push({
        id: 'done-search',
        kind: 'search',
        title: `Searching ${String(queries[0]).slice(0, 64)}`,
        status: 'done',
        badge: `${queries.length} quer${queries.length === 1 ? 'y' : 'ies'}`,
        children: queries.map((q) => ({ type: 'query', text: q })),
      });
    }
    if (sources.length) {
      steps.push({
        id: 'done-reading',
        kind: 'reading',
        title: `Reading ${sources.length} page${sources.length === 1 ? '' : 's'}`,
        status: 'done',
        badge: `${sources.length} page${sources.length === 1 ? '' : 's'}`,
        children: sources.slice(0, 12).map((s) => ({
          type: 'page',
          title: s.title || s.url,
          url: s.url,
          domain: domainFromUrl(s.url),
        })),
      });
    }
  }

  const visible = steps.filter((s) => s.status !== 'pending' || streaming);
  const activeStep = visible.find((s) => s.status === 'active');
  let headerLabel = '';
  if (agentMode) {
    headerLabel = streaming
      ? activeStep?.title
        ? activeStep.title
        : 'Agent arbeitet…'
      : visible.length
        ? 'Agent fertig'
        : '';
  } else if (streaming) {
    headerLabel =
      activeStep?.kind === 'thinking'
        ? 'Nachdenken…'
        : activeStep?.title || 'Recherche…';
  } else if (visible.length) {
    headerLabel = 'Fertig';
  }

  return {
    headerLabel,
    steps: visible,
    sources,
    queries,
    streaming,
    agentMode: Boolean(agentMode),
  };
}

const KindIcon = ({ kind, className = 'w-3.5 h-3.5' }) => {
  if (kind === 'thinking') return <Lightbulb className={className} />;
  if (kind === 'search') return <Search className={className} />;
  if (kind === 'reading') return <Globe className={className} />;
  if (kind === 'image') return <ImagePlus className={className} />;
  if (kind === 'sound') return <Volume2 className={className} />;
  if (kind === 'pdf') return <FileText className={className} />;
  if (kind === 'terminal') return <Terminal className={className} />;
  if (kind === 'code') return <Code2 className={className} />;
  if (kind === 'ask') return <MessageCircle className={className} />;
  return <Wrench className={className} />;
};

const thinkingTextFromStep = (step) =>
  (step?.children || [])
    .filter((c) => c && c.type === 'think' && c.text)
    .map((c) => String(c.text))
    .join('\n\n')
    .trim();

function CopyTextButton({ text = '', className = '', compact = false, title = 'Thinking kopieren' }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const handleCopy = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const value = String(text || '').trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (_) {
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_2) {
        return;
      }
    }
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1800);
  };

  const disabled = !String(text || '').trim();

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={disabled}
      title={disabled ? 'Noch kein Thinking-Text' : title}
      className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] transition-colors disabled:opacity-35 disabled:cursor-not-allowed ${
        copied
          ? 'text-emerald-300'
          : 'text-amber-300/90 hover:bg-amber-500/15 hover:text-amber-200'
      } ${className}`}
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {!compact ? (copied ? 'Kopiert' : 'Kopieren') : null}
    </button>
  );
}

/**
 * Thinking-Text stream-sicher: nur anhängen (kein Re-Render des ganzen Texts),
 * bei aktiver Markierung Updates pausieren — Auswahl bleibt, auch mitten im Text.
 * Scroll: bleibt wo der Nutzer liest; nur ans Ende springen wenn schon unten.
 */
function SelectableThinkBody({ text = '' }) {
  const preRef = useRef(null);
  const shownRef = useRef('');
  const selectingRef = useRef(false);
  const pointerDownRef = useRef(false);
  const pendingRef = useRef(null);
  const stickThinkBottomRef = useRef(true);

  const setSelecting = (active) => {
    if (selectingRef.current === active) return;
    selectingRef.current = active;
    try {
      window.dispatchEvent(
        new CustomEvent('odium:ai-text-selecting', { detail: { active: !!active } })
      );
    } catch (_) {
      /* ignore */
    }
  };

  const nearThinkBottom = (el) => {
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 28;
  };

  const syncText = (nextRaw) => {
    const el = preRef.current;
    if (!el) return;
    const next = String(nextRaw || '');
    const shown = shownRef.current;
    if (next === shown) return;

    const stick = stickThinkBottomRef.current || nearThinkBottom(el);
    const savedTop = el.scrollTop;

    if (next.length >= shown.length && (shown === '' || next.startsWith(shown))) {
      const delta = next.slice(shown.length);
      if (delta) el.appendChild(document.createTextNode(delta));
      shownRef.current = next;
    } else {
      el.textContent = next;
      shownRef.current = next;
    }

    if (stick) {
      el.scrollTop = el.scrollHeight;
      stickThinkBottomRef.current = true;
    } else {
      el.scrollTop = savedTop;
    }
  };

  useEffect(() => {
    if (selectingRef.current) {
      pendingRef.current = text;
      return;
    }
    syncText(text);
  }, [text]);

  useEffect(() => {
    const el = preRef.current;
    if (!el) return;

    const selectionInside = () => {
      const sel = window.getSelection?.();
      if (!sel || sel.isCollapsed || sel.rangeCount < 1) return false;
      const a = sel.anchorNode;
      const f = sel.focusNode;
      return !!(a && f && el.contains(a) && el.contains(f));
    };

    const beginSelect = () => {
      pointerDownRef.current = true;
      stickThinkBottomRef.current = false;
      setSelecting(true);
    };

    const endSelectIfDone = () => {
      if (!selectingRef.current) return;
      // Während Drag offen halten — sonst fliegt die Markierung weg
      if (pointerDownRef.current) return;
      if (selectionInside()) return;
      setSelecting(false);
      if (pendingRef.current != null) {
        const pending = pendingRef.current;
        pendingRef.current = null;
        syncText(pending);
      }
    };

    const onPointerUp = () => {
      pointerDownRef.current = false;
      window.setTimeout(endSelectIfDone, 0);
    };

    const onWheel = (e) => {
      if (e.deltaY < 0) stickThinkBottomRef.current = false;
      else if (nearThinkBottom(el)) stickThinkBottomRef.current = true;
    };

    const onScroll = () => {
      stickThinkBottomRef.current = nearThinkBottom(el);
    };

    el.addEventListener('mousedown', beginSelect);
    el.addEventListener('selectstart', beginSelect);
    el.addEventListener('wheel', onWheel, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mouseup', onPointerUp);
    document.addEventListener('keyup', endSelectIfDone);
    document.addEventListener('selectionchange', endSelectIfDone);
    return () => {
      el.removeEventListener('mousedown', beginSelect);
      el.removeEventListener('selectstart', beginSelect);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('keyup', endSelectIfDone);
      document.removeEventListener('selectionchange', endSelectIfDone);
      if (selectingRef.current) setSelecting(false);
    };
  }, []);

  return (
    <pre
      ref={preRef}
      className="ai-think-stream max-h-64 overflow-auto whitespace-pre-wrap break-words font-sans text-[11px] leading-relaxed text-neutral-300 m-0 select-text cursor-text"
    />
  );
}

const StatusDot = ({ status }) => {
  if (status === 'active') {
    return (
      <span className="relative flex h-4 w-4 items-center justify-center flex-shrink-0">
        <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rose-500/90 flex-shrink-0">
        <span className="text-[9px] font-bold text-white leading-none">!</span>
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full border border-neutral-600 bg-transparent flex-shrink-0" />
    );
  }
  return (
    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 flex-shrink-0">
      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
    </span>
  );
};

const UndoChangeButton = ({
  revisionId,
  stepId,
  projectId,
  rejected = false,
  rejectedIds,
  rejectingId,
  onReject,
  label = 'Rückgängig',
  className = '',
}) => {
  const rid = String(revisionId || '');
  const isRejected = rejected || rejectedIds.has(rid);
  const canReject = projectId && rid && !isRejected;
  const isRejecting = rejectingId === String(stepId) || rejectingId === rid;

  if (isRejected) {
    return (
      <span
        className={`text-[9px] uppercase tracking-wide text-rose-400/90 flex-shrink-0 ${className}`}
      >
        rückgängig
      </span>
    );
  }
  if (!canReject) return null;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onReject(revisionId, stepId);
      }}
      disabled={!!rejectingId}
      className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] text-rose-300 hover:bg-rose-500/15 disabled:opacity-40 flex-shrink-0 ${className}`}
      title="KI-Änderung rückgängig machen und vorherigen Inhalt wiederherstellen"
    >
      {isRejecting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
      {label}
    </button>
  );
};

/**
 * DeepSeek-ähnliche Research-/Thinking-Anzeige.
 */
const AiResearchProcess = ({
  pipeline,
  phase,
  phaseMessage,
  agentSteps,
  search,
  streaming = false,
  elapsedLabel = '',
  thinkingText = '',
  agentMode = false,
  projectId = '',
  onOpenDiff,
  onRevisionRejected,
}) => {
  const model = useMemo(
    () =>
      buildResearchModel({
        pipeline,
        phase,
        phaseMessage,
        agentSteps,
        search,
        streaming,
        thinkingText,
        agentMode,
      }),
    [pipeline, phase, phaseMessage, agentSteps, search, streaming, thinkingText, agentMode]
  );

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('process');
  const [expanded, setExpanded] = useState({});
  const [rejectedIds, setRejectedIds] = useState(() => new Set());
  const [rejectingId, setRejectingId] = useState('');
  const [rejectError, setRejectError] = useState('');
  /** Nutzer hat Panel/Schritte bewusst zugeklappt → nicht wieder aufzwingen */
  const userClosedPanelRef = useRef(false);
  const userCollapsedStepsRef = useRef(new Set());
  const userOpenedStepsRef = useRef(new Set());
  const autoOpenedActiveRef = useRef(null);

  const rejectRevision = async (revisionId, stepId) => {
    if (!projectId || !revisionId || rejectingId) return;
    setRejectError('');
    setRejectingId(String(stepId || revisionId));
    try {
      const res = await axios.post(
        `/api/ai/projects/${encodeURIComponent(projectId)}/file/restore`,
        { revisionId },
        { headers: authHeaders() }
      );
      const restored = String(res.data?.file?.content ?? '');
      const restoredPath = String(res.data?.file?.path || res.data?.file?.restoredPath || '').replace(
        /\\/g,
        '/'
      );
      const deleted = !!res.data?.file?.deleted;
      setRejectedIds((prev) => new Set(prev).add(String(revisionId)));
      onRevisionRejected?.(revisionId);
      try {
        window.dispatchEvent(
          new CustomEvent('odium:agent-file-live', {
            detail: {
              projectId,
              path: restoredPath,
              content: restored,
              done: true,
              stage: 'done',
              rejected: true,
              diff: null,
            },
          })
        );
        window.dispatchEvent(
          new CustomEvent('odium:agent-change-rejected', {
            detail: { projectId, path: restoredPath, revisionId, deleted },
          })
        );
      } catch (_) {
        /* ignore */
      }
    } catch (err) {
      setRejectError(
        err.response?.data?.error || err.message || 'Rückgängig machen fehlgeschlagen'
      );
    } finally {
      setRejectingId('');
    }
  };

  const undoableRevisions = useMemo(() => {
    const list = [];
    model.steps.forEach((step) => {
      const rid = String(step.revisionId || '');
      if (!rid || step.rejected || rejectedIds.has(rid)) return;
      list.push({ revisionId: rid, stepId: step.id, path: step.filePath || '' });
      (step.children || []).forEach((child) => {
        const crid = String(child.revisionId || '');
        if (!crid || child.rejected || rejectedIds.has(crid) || crid === rid) return;
        list.push({ revisionId: crid, stepId: step.id, path: child.path || step.filePath || '' });
      });
    });
    const seen = new Set();
    return list.filter((item) => {
      if (seen.has(item.revisionId)) return false;
      seen.add(item.revisionId);
      return true;
    });
  }, [model.steps, rejectedIds]);

  useEffect(() => {
    // Panel nur beim Start des Streams öffnen — nicht erneut, wenn Nutzer zugeklappt hat
    if (streaming && !userClosedPanelRef.current) {
      setOpen(true);
    }
  }, [streaming]);

  useEffect(() => {
    setExpanded((prev) => {
      const next = { ...prev };
      let activeId = null;
      const hasVisibleFileDiff = (s) =>
        Array.isArray(s?.children) &&
        s.children.some((c) => c && (c.type === 'diff' || (c.type === 'code' && c.path)));

      model.steps.forEach((s) => {
        if (s.status === 'active') activeId = s.id;

        if (userCollapsedStepsRef.current.has(s.id)) {
          next[s.id] = false;
          return;
        }
        if (userOpenedStepsRef.current.has(s.id)) {
          next[s.id] = true;
          return;
        }
        // Datei-Diffs / Code-Änderungen sichtbar lassen (sonst klappt der Schritt zu, bevor man den Diff sieht)
        if (hasVisibleFileDiff(s) && (s.status === 'done' || s.status === 'error' || s.status === 'active')) {
          next[s.id] = true;
          return;
        }
        // Default: zugeklappt. Nur der aktuell laufende Schritt kurz auf.
        if (s.status === 'active' && s.children?.length) {
          next[s.id] = true;
        } else if (next[s.id] == null) {
          next[s.id] = false;
        } else if (s.status === 'done' || s.status === 'error') {
          // Auto-geöffnete Active-Schritte nach Abschluss wieder zu
          if (autoOpenedActiveRef.current === s.id) {
            next[s.id] = false;
          }
        }
      });
      if (activeId && !userCollapsedStepsRef.current.has(activeId) && !userOpenedStepsRef.current.has(activeId)) {
        autoOpenedActiveRef.current = activeId;
      } else if (!activeId && autoOpenedActiveRef.current) {
        const finished = autoOpenedActiveRef.current;
        const finishedStep = model.steps.find((s) => s.id === finished);
        if (
          !userOpenedStepsRef.current.has(finished) &&
          !userCollapsedStepsRef.current.has(finished)
        ) {
          // Diff-Schritte offen lassen
          next[finished] = hasVisibleFileDiff(finishedStep);
        }
        autoOpenedActiveRef.current = null;
      }
      return next;
    });
  }, [model.steps]);

  if (!model.steps.length && !model.sources.length && !streaming) return null;

  const stepCount = model.steps.length;
  const sourceCount = model.sources.length;

  const togglePanel = () => {
    setOpen((v) => {
      const next = !v;
      if (!next) userClosedPanelRef.current = true;
      else userClosedPanelRef.current = false;
      return next;
    });
  };

  const toggleStep = (id) => {
    setExpanded((prev) => {
      const willOpen = !prev[id];
      if (willOpen) {
        userOpenedStepsRef.current.add(id);
        userCollapsedStepsRef.current.delete(id);
      } else {
        userCollapsedStepsRef.current.add(id);
        userOpenedStepsRef.current.delete(id);
      }
      return { ...prev, [id]: willOpen };
    });
  };

  return (
    <div className="ai-research rounded-2xl border border-neutral-800 bg-[#121212] text-neutral-100 overflow-hidden shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
      <button
        type="button"
        onClick={togglePanel}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
      >
        <ScanSearch className="w-4 h-4 text-neutral-300 flex-shrink-0" />
        <span className="text-[13px] font-medium text-white truncate min-w-0">
          {model.headerLabel ||
            (streaming ? (agentMode ? 'Agent arbeitet…' : 'Recherche…') : agentMode ? 'Agent' : 'Recherche')}
        </span>
        <span className="inline-flex items-center rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-400 tabular-nums flex-shrink-0">
          {stepCount} {agentMode ? (stepCount === 1 ? 'Schritt' : 'Schritte') : `step${stepCount === 1 ? '' : 's'}`}
        </span>
        <span className="inline-flex items-center rounded-full bg-neutral-800 px-2 py-0.5 text-[10px] text-neutral-400 tabular-nums flex-shrink-0">
          {sourceCount} source{sourceCount === 1 ? '' : 's'}
        </span>
        {elapsedLabel ? (
          <span className="hidden sm:inline text-[10px] text-neutral-500 tabular-nums flex-shrink-0 ml-auto mr-1">
            {elapsedLabel}
          </span>
        ) : (
          <span className="flex-1" />
        )}
        {open ? (
          <ChevronUp className="w-4 h-4 text-neutral-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-neutral-500 flex-shrink-0" />
        )}
      </button>

      {open ? (
        <div className="px-3 pb-3 pt-0 space-y-2.5">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setTab('process')}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                tab === 'process'
                  ? 'bg-neutral-800 text-white'
                  : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-900'
              }`}
            >
              <ScanSearch className="w-3 h-3" />
              {agentMode ? 'Agent-Schritte' : 'Research Process'}
              <span className="tabular-nums text-neutral-500">{stepCount}</span>
            </button>
            <button
              type="button"
              onClick={() => setTab('sources')}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                tab === 'sources'
                  ? 'bg-neutral-800 text-white'
                  : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-900'
              }`}
            >
              <Globe className="w-3 h-3" />
              Sources
              <span className="tabular-nums text-neutral-500">{sourceCount}</span>
            </button>
          </div>

          {rejectError ? (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-2.5 py-1.5 text-[11px] text-rose-300">
              {rejectError}
            </div>
          ) : null}

          {agentMode && tab === 'process' && undoableRevisions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-neutral-800 bg-neutral-900/60 px-2.5 py-2">
              <span className="text-[10px] text-neutral-400 w-full sm:w-auto">Rückgängig:</span>
              {undoableRevisions.map((item) => (
                <button
                  key={item.revisionId}
                  type="button"
                  onClick={() => rejectRevision(item.revisionId, item.stepId)}
                  disabled={!!rejectingId}
                  className="inline-flex items-center gap-1 rounded-full border border-rose-500/25 bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-200 hover:bg-rose-500/20 disabled:opacity-40"
                  title={
                    item.path
                      ? `Änderung an ${item.path} rückgängig machen`
                      : 'KI-Änderung rückgängig machen'
                  }
                >
                  {rejectingId === String(item.revisionId) || rejectingId === String(item.stepId) ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Undo2 className="w-3 h-3" />
                  )}
                  <span className="font-mono truncate max-w-[10rem]">
                    {item.path ? item.path.split('/').pop() : 'Datei'}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {tab === 'process' ? (
            <ul className="relative space-y-0 pl-0.5">
              {model.steps.map((step, idx) => {
                const isLast = idx === model.steps.length - 1;
                const isOpen = !!expanded[step.id];
                const hasChildren = Array.isArray(step.children) && step.children.length > 0;
                return (
                  <li
                    key={step.id}
                    className={`relative flex gap-2.5 rounded-lg transition-colors ${
                      step.status === 'active'
                        ? 'bg-emerald-500/10 ring-1 ring-emerald-500/30 -mx-1 px-1'
                        : ''
                    }`}
                  >
                    {!isLast ? (
                      <span
                        className="absolute left-[7px] top-5 bottom-0 w-px bg-neutral-800"
                        aria-hidden
                      />
                    ) : null}
                    <div className="relative z-[1] pt-0.5">
                      <StatusDot status={step.status} />
                    </div>
                    <div className="flex-1 min-w-0 pb-3">
                      <div className="flex items-start gap-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => (hasChildren ? toggleStep(step.id) : undefined)}
                          className={`flex-1 flex items-center gap-1.5 text-left min-w-0 ${
                            hasChildren ? 'cursor-pointer' : 'cursor-default'
                          }`}
                          disabled={!hasChildren}
                        >
                          <KindIcon
                            kind={step.kind}
                            className={`w-3.5 h-3.5 flex-shrink-0 ${
                              step.status === 'active' ? 'text-emerald-400' : 'text-neutral-400'
                            }`}
                          />
                          <span
                            className={`text-[12px] font-medium truncate min-w-0 ${
                              step.status === 'active' ? 'text-emerald-100' : 'text-neutral-100'
                            }`}
                          >
                            {step.title}
                          </span>
                          {step.badge ? (
                            <span
                              className={`ml-auto inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] tabular-nums flex-shrink-0 ${
                                step.status === 'done' && /fertig|abgeschlossen/i.test(String(step.badge))
                                  ? 'bg-emerald-500/15 text-emerald-400/90'
                                  : 'bg-neutral-800/90 text-neutral-400'
                              }`}
                            >
                              {step.badge}
                            </span>
                          ) : (
                            <span className="flex-1" />
                          )}
                          {hasChildren ? (
                            isOpen ? (
                              <ChevronUp className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                            )
                          ) : null}
                        </button>
                        {step.revisionId ? (
                          <UndoChangeButton
                            revisionId={step.revisionId}
                            stepId={step.id}
                            projectId={projectId}
                            rejected={step.rejected}
                            rejectedIds={rejectedIds}
                            rejectingId={rejectingId}
                            onReject={rejectRevision}
                            className="mt-0.5"
                          />
                        ) : step.kind === 'thinking' ? (
                          <CopyTextButton
                            text={thinkingTextFromStep(step)}
                            compact
                            className="mt-0.5"
                            title="Gesamtes Thinking dieses Schritts kopieren"
                          />
                        ) : null}
                      </div>

                      {isOpen && hasChildren ? (
                        <div className="mt-1.5 rounded-xl bg-neutral-900/80 border border-neutral-800/80 p-1.5 space-y-1">
                          {step.children.map((child, cIdx) => {
                            if (child.type === 'query') {
                              return (
                                <div
                                  key={`q-${cIdx}`}
                                  className="flex items-start gap-1.5 rounded-lg bg-neutral-800/70 px-2 py-1.5 text-[11px] text-neutral-200"
                                >
                                  <Search className="w-3 h-3 mt-0.5 text-neutral-400 flex-shrink-0" />
                                  <span className="min-w-0 break-words">{child.text}</span>
                                </div>
                              );
                            }
                            if (child.type === 'think') {
                              return (
                                <div
                                  key={`th-${step.id}-${cIdx}`}
                                  className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-2.5 py-2 text-[11px] text-neutral-200"
                                  onMouseDown={(e) => e.stopPropagation()}
                                >
                                  <div className="mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wide text-amber-400/90 select-none">
                                    <Lightbulb className="w-3 h-3" />
                                    Thinking
                                    <CopyTextButton
                                      text={child.text}
                                      compact
                                      className="ml-auto normal-case tracking-normal"
                                      title="Thinking-Text kopieren"
                                    />
                                  </div>
                                  <SelectableThinkBody text={child.text} />
                                </div>
                              );
                            }
                            if (child.type === 'page') {
                              return (
                                <a
                                  key={`p-${cIdx}-${child.url}`}
                                  href={child.url || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 rounded-lg bg-neutral-800/70 px-2 py-1.5 text-[11px] text-neutral-200 hover:bg-neutral-800 transition-colors"
                                >
                                  {faviconUrl(child.url) ? (
                                    <img
                                      src={faviconUrl(child.url)}
                                      alt=""
                                      className="w-3.5 h-3.5 rounded-sm flex-shrink-0 bg-neutral-700"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <Globe className="w-3.5 h-3.5 text-neutral-500 flex-shrink-0" />
                                  )}
                                  <span className="min-w-0 flex-1 truncate">{child.title}</span>
                                  {child.domain ? (
                                    <span className="text-[10px] text-neutral-500 flex-shrink-0 truncate max-w-[7rem]">
                                      {child.domain}
                                    </span>
                                  ) : null}
                                </a>
                              );
                            }
                            if (child.type === 'file') {
                              return (
                                <div
                                  key={`f-${cIdx}`}
                                  className="flex items-center gap-1.5 rounded-lg bg-neutral-800/70 px-2 py-1.5 text-[11px] text-neutral-200"
                                >
                                  <FileText className="w-3 h-3 text-emerald-400/80 flex-shrink-0" />
                                  <span className="font-mono text-[11px] text-emerald-200/90 truncate min-w-0">
                                    {child.path || child.text}
                                  </span>
                                  {child.revisionId ? (
                                    <UndoChangeButton
                                      revisionId={child.revisionId}
                                      stepId={step.id}
                                      projectId={projectId}
                                      rejected={child.rejected}
                                      rejectedIds={rejectedIds}
                                      rejectingId={rejectingId}
                                      onReject={rejectRevision}
                                      className="ml-auto"
                                    />
                                  ) : null}
                                </div>
                              );
                            }
                            if (child.type === 'diff') {
                              const dLines = Array.isArray(child.lines) ? child.lines : [];
                              return (
                                <div
                                  key={`d-${cIdx}`}
                                  className="overflow-hidden rounded-lg border border-neutral-700/80 bg-[#0c0f14]"
                                >
                                  <div
                                    className={`flex items-center gap-1.5 border-b border-neutral-800 px-2 py-1 bg-[#12161e] ${
                                      child.path && onOpenDiff
                                        ? 'cursor-pointer hover:bg-[#161b24]'
                                        : ''
                                    }`}
                                    role={child.path && onOpenDiff ? 'button' : undefined}
                                    tabIndex={child.path && onOpenDiff ? 0 : undefined}
                                    onClick={(e) => {
                                      if (!child.path || !onOpenDiff) return;
                                      if (e.target.closest('button')) return;
                                      onOpenDiff(child.path, {
                                        diff: {
                                          lines: dLines,
                                          created: !!child.created,
                                          unchanged: false,
                                        },
                                        revisionId: child.revisionId,
                                        content: child.content,
                                      });
                                    }}
                                    onKeyDown={(e) => {
                                      if (!child.path || !onOpenDiff) return;
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpenDiff(child.path, {
                                          diff: {
                                            lines: dLines,
                                            created: !!child.created,
                                            unchanged: false,
                                          },
                                          revisionId: child.revisionId,
                                          content: child.content,
                                        });
                                      }
                                    }}
                                  >
                                    <Code2 className="w-3 h-3 text-sky-400/80 flex-shrink-0" />
                                    <span
                                      className="font-mono text-[10px] text-sky-200/90 truncate min-w-0"
                                      title={child.path || ''}
                                    >
                                      {child.path || 'diff'}
                                    </span>
                                    {child.revisionId ? (
                                      <UndoChangeButton
                                        revisionId={child.revisionId}
                                        stepId={step.id}
                                        projectId={projectId}
                                        rejected={child.rejected}
                                        rejectedIds={rejectedIds}
                                        rejectingId={rejectingId}
                                        onReject={rejectRevision}
                                        className="ml-auto"
                                      />
                                    ) : (
                                      <span className="ml-auto text-[9px] uppercase tracking-wide text-neutral-500 flex-shrink-0">
                                        {child.created ? 'neu' : 'diff'}
                                      </span>
                                    )}
                                  </div>
                                  <pre className="max-h-64 overflow-auto px-0 py-1 text-[11px] leading-[1.45] font-mono whitespace-pre">
                                    <code>
                                      {dLines.map((line, li) => {
                                        const op = line?.op || ' ';
                                        const text = String(line?.text ?? '');
                                        const bg =
                                          op === '+'
                                            ? 'bg-emerald-500/10 text-emerald-200'
                                            : op === '-'
                                              ? 'bg-rose-500/10 text-rose-200'
                                              : op === 'meta'
                                                ? 'text-neutral-500 italic'
                                                : 'text-neutral-400';
                                        const prefix =
                                          op === 'meta' ? '' : op === ' ' ? ' ' : op;
                                        return (
                                          <div
                                            key={`dl-${li}`}
                                            className={`px-2.5 ${bg}`}
                                          >
                                            {op === 'meta' ? text : `${prefix}${text}` || ' '}
                                          </div>
                                        );
                                      })}
                                    </code>
                                  </pre>
                                </div>
                              );
                            }
                            if (child.type === 'code') {
                              const lines = String(child.text || '').replace(/\n$/, '').split('\n');
                              return (
                                <div
                                  key={`c-${cIdx}`}
                                  className="overflow-hidden rounded-lg border border-neutral-700/80 bg-[#0c0f14]"
                                >
                                  <div className="flex items-center gap-1.5 border-b border-neutral-800 px-2 py-1 bg-[#12161e]">
                                    <Code2 className="w-3 h-3 text-emerald-400/80 flex-shrink-0" />
                                    <span
                                      className="font-mono text-[10px] text-emerald-200/90 truncate min-w-0"
                                      title={child.path || ''}
                                    >
                                      {child.path || 'code'}
                                    </span>
                                    {child.revisionId ? (
                                      <UndoChangeButton
                                        revisionId={child.revisionId}
                                        stepId={step.id}
                                        projectId={projectId}
                                        rejected={child.rejected}
                                        rejectedIds={rejectedIds}
                                        rejectingId={rejectingId}
                                        onReject={rejectRevision}
                                        className="ml-auto"
                                      />
                                    ) : child.language ? (
                                      <span className="ml-auto text-[9px] uppercase tracking-wide text-neutral-500 flex-shrink-0">
                                        {child.language}
                                      </span>
                                    ) : null}
                                  </div>
                                  <pre className="max-h-56 overflow-auto px-2.5 py-2 text-[11px] leading-[1.5] text-neutral-300 font-mono whitespace-pre">
                                    <code>
                                      {lines.map((line, li) => (
                                        <div key={`cl-${li}`}>{line || ' '}</div>
                                      ))}
                                    </code>
                                  </pre>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={`n-${cIdx}`}
                                className="flex items-start gap-1.5 rounded-lg bg-neutral-800/70 px-2 py-1.5 text-[11px] text-neutral-300"
                              >
                                <FileText className="w-3 h-3 mt-0.5 text-neutral-500 flex-shrink-0" />
                                <span className="min-w-0 break-words whitespace-pre-wrap">{child.text}</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
              {!model.steps.length ? (
                <li className="text-[11px] text-neutral-500 px-1 py-2">Noch keine Schritte…</li>
              ) : null}
            </ul>
          ) : (
            <ul className="space-y-1">
              {model.sources.length ? (
                model.sources.map((src) => (
                  <li key={src.url}>
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 rounded-xl bg-neutral-900/80 border border-neutral-800/80 px-2.5 py-2 text-[11px] text-neutral-200 hover:bg-neutral-900 transition-colors"
                    >
                      {faviconUrl(src.url) ? (
                        <img
                          src={faviconUrl(src.url)}
                          alt=""
                          className="w-4 h-4 rounded-sm flex-shrink-0 bg-neutral-700"
                          loading="lazy"
                        />
                      ) : (
                        <Globe className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 truncate font-medium">{src.title || src.url}</span>
                      <span className="text-[10px] text-neutral-500 flex-shrink-0 truncate max-w-[8rem]">
                        {domainFromUrl(src.url)}
                      </span>
                    </a>
                  </li>
                ))
              ) : (
                <li className="text-[11px] text-neutral-500 px-1 py-2">Noch keine Quellen.</li>
              )}
              {search?.error ? (
                <li className="text-[11px] text-rose-400 px-1">{search.error}</li>
              ) : null}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default AiResearchProcess;
